import React, { Component, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { SIZE_FIELD_KEYS } from '../../util/listingDisplay';
import { isFilterEnabled } from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { makeGetListingsByIdSelector } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, OutsideClickHandler } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  initialValues,
  validUrlQueryParamsFromProps,
  getDerivedRenderData,
  onResetAll,
  createFilterValueChangeHandler,
  onSortBy,
} from './SearchPage.shared';

import filterIcon from '../../assets/search/icon-filter.svg';
import sortIcon from '../../assets/search/icon-sort.svg';

import FilterComponent from './FilterComponent';
import ActiveFilterChips from './ActiveFilterChips';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import SearchPageAccessWrapper from './SearchPageAccessWrapper';
import SearchErrors from './SearchErrors';

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      showAvailabilityNOfferTypeFilterDropdown: false,
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config, location } = this.props;
    onResetAll({
      history,
      routeConfiguration,
      config,
      location,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      setState: this.setState.bind(this),
    });
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;

    return createFilterValueChangeHandler(
      {
        history,
        routeConfiguration,
        config,
        location,
        currentPathParams,
        urlQueryParams: validUrlQueryParamsFromProps(this.props),
        setState: this.setState.bind(this),
        getState: () => this.state,
      },
      useHistoryPush
    );
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    onSortBy({
      history,
      routeConfiguration,
      location,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      urlParam,
      values,
    });
  }

  toggleAvailabilityNOfferTypeFilterDropdown = () => {
    this.setState(prevState => ({
      showAvailabilityNOfferTypeFilterDropdown: !prevState.showAvailabilityNOfferTypeFilterDropdown,
    }));
  };

  closeAvailabilityNOfferTypeFilterDropdown = () => {
    if (this.state.showAvailabilityNOfferTypeFilterDropdown) {
      this.setState({ showAvailabilityNOfferTypeFilterDropdown: false });
    }
  };

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
    } = this.props;

    const {
      listingTypePathParam,
      sortConfig,
      validQueryParams,
      availableFilters,
      selectedFilters,
      isValidDatesFilter,
      selectedFiltersCountForMobile,
      totalItems,
      listingsAreLoaded,
      conflictingFilterActive,
      showCreateListingsLink,
      title,
      description,
      schema,
      marketplaceCurrency,
      listingCategories,
    } = getDerivedRenderData({
      intl,
      location,
      config,
      routeConfiguration,
      searchParams,
      pagination,
      listings,
      searchInProgress,
      currentPathParams,
      currentUser,
    });

    const { showAvailabilityNOfferTypeFilterDropdown } = this.state;
    const sizeFilters = (config?.listing?.listingFields || [])
      .filter(field => SIZE_FIELD_KEYS.includes(field.key) && isFilterEnabled(field.filterConfig))
      .map(field => ({
        ...field,
        filterConfig: {
          ...field.filterConfig,
          label: field.filterConfig?.label || field.label,
        },
      }));
    const toolbarFilters = availableFilters.filter(
      filterConfig => !SIZE_FIELD_KEYS.includes(filterConfig.key)
    );

    // Walk the selected category path (pub_categoryLevel1, ...2, ...) down to the
    // deepest chosen level. The last name becomes the page heading; the ones above
    // it form the trail shown beneath.
    const categoryTrail = [];
    let categoryOptions = listingCategories || [];
    for (let level = 1; categoryOptions.length > 0; level++) {
      const selectedId = validQueryParams[`pub_categoryLevel${level}`];
      const match = selectedId ? categoryOptions.find(c => c.id === selectedId) : null;
      if (!match) {
        break;
      }
      categoryTrail.push(match.name);
      categoryOptions = match.subcategories || [];
    }
    const categoryHeading = categoryTrail[categoryTrail.length - 1] || null;

    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          variant={mode === 'desktop' || mode === 'mobile' ? 'pill' : undefined}
          labelId={`${mode}-search-page-sort-by`}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
      />
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        <div className={classNames(css.layoutWrapperContainer, css.layoutGrid)}>
          <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
            <div className={css.toolbar}>
              <div className={css.filterCluster}>
                <OutsideClickHandler
                  rootClassName={css.filterTrigger}
                  onOutsideClick={this.closeAvailabilityNOfferTypeFilterDropdown}
                >
                  <div
                    className={css.toolbarLabel}
                    onClick={this.toggleAvailabilityNOfferTypeFilterDropdown}
                  >
                    <img
                      className={css.toolbarIcon}
                      src={filterIcon}
                      alt=""
                      width={20}
                      height={20}
                    />
                    <FormattedMessage id="SearchPage.filterLabel" />
                  </div>

                  {showAvailabilityNOfferTypeFilterDropdown ? (
                    <div className={css.avalabiltyNofferTypeFilterDropdown}>
                      <div className={css.dropdownFilterPills}>
                        <FilterComponent
                          key="dates"
                          id="SearchPageWithGrid.AvailabilityFilter"
                          config={{
                            key: 'dates',
                            schemaType: 'dates',
                            label: intl.formatMessage({ id: 'FilterComponent.datesLabel' }),
                            dateRangeMode: 'day',
                          }}
                          containerId="SearchPageWithGrid_AvailabilityOfferTypeFilters"
                          marketplaceCurrency={marketplaceCurrency}
                          urlQueryParams={validQueryParams}
                          initialValues={initialValues(this.props, this.state.currentQueryParams)}
                          getHandleChangedValueFn={this.getHandleChangedValueFn}
                          intl={intl}
                          showAsPopup
                          openerVariant="pill"
                          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
                        />
                        {listingTypePathParam ? null : (
                          <FilterComponent
                            key="listingType"
                            id="SearchPageWithGrid.OfferTypeFilter"
                            config={{
                              key: 'listingType',
                              schemaType: 'listingType',
                              scope: 'public',
                              options:
                                config?.listing?.listingTypes?.map(lt => ({
                                  option: lt.listingType,
                                  label: lt.label,
                                })) || [],
                            }}
                            containerId="SearchPageWithGrid_AvailabilityOfferTypeFilters"
                            marketplaceCurrency={marketplaceCurrency}
                            urlQueryParams={validQueryParams}
                            initialValues={initialValues(this.props, this.state.currentQueryParams)}
                            getHandleChangedValueFn={this.getHandleChangedValueFn}
                            intl={intl}
                            showAsPopup
                            openerVariant="pill"
                            contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
                          />
                        )}
                        {sizeFilters.map(filterConfig => (
                          <FilterComponent
                            key={`SearchPageWithGrid.size.${filterConfig.key}`}
                            id={`SearchPageWithGrid.${filterConfig.key}`}
                            config={filterConfig}
                            containerId="SearchPageWithGrid_AvailabilityOfferTypeFilters"
                            listingCategories={listingCategories}
                            marketplaceCurrency={marketplaceCurrency}
                            urlQueryParams={validQueryParams}
                            initialValues={initialValues(this.props, this.state.currentQueryParams)}
                            getHandleChangedValueFn={this.getHandleChangedValueFn}
                            intl={intl}
                            showAsPopup
                            openerVariant="pill"
                            contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
                          />
                        ))}
                      </div>
                    </div>
                  ) : null}
                </OutsideClickHandler>

                <span className={css.toolbarDivider} aria-hidden="true" />
                <div className={css.filterPills} data-testid="filterList">
                  {toolbarFilters.map(filterConfig => {
                    const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
                      filterConfig.key
                    }`;
                    const filterId = `SearchFiltersDesktop.${filterConfig.key.toLowerCase()}`;
                    return (
                      <FilterComponent
                        key={key}
                        id={filterId}
                        className={css.filter}
                        config={filterConfig}
                        containerId="SearchPageWithGrid_DesktopFilters"
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        showAsPopup
                        openerVariant="pill"
                        contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
                      />
                    );
                  })}
                </div>
                {selectedFiltersCountForMobile > 0 ? (
                  <button className={css.clearAllButton} onClick={e => this.handleResetAll(e)}>
                    <FormattedMessage id="SearchPage.clearAll" />
                  </button>
                ) : null}
              </div>
              <div className={css.sortCluster}>
                {sortConfig.active ? (
                  <>
                    <div className={css.toolbarLabel}>
                      <img
                        className={css.toolbarIconSort}
                        src={sortIcon}
                        alt=""
                        width={24}
                        height={24}
                      />
                      <FormattedMessage id="MainPanelHeader.sortBy" />
                    </div>
                    <span className={css.toolbarDivider} aria-hidden="true" />
                    {sortBy('desktop')}
                  </>
                ) : null}
              </div>
            </div>
            <div className={css.chipsRow}>
              <ActiveFilterChips
                availableFilters={availableFilters}
                urlQueryParams={validQueryParams}
                getHandleChangedValueFn={this.getHandleChangedValueFn}
              />
              {listingsAreLoaded ? (
                <p className={css.resultsCount}>
                  <FormattedMessage
                    id="SearchPage.resultsInParens"
                    values={{ count: totalItems }}
                  />
                </p>
              ) : null}
            </div>
          </aside>

          <div id="main-content" className={css.layoutWrapperMain} role="main">
            <div className={css.searchResultContainer}>
              <h1 className={css.visuallyHidden}>
                {categoryHeading || <FormattedMessage id="SearchPage.allListingsHeading" />}
              </h1>
              <SearchFiltersMobile
                className={css.searchFiltersMobileList}
                urlQueryParams={validQueryParams}
                sortByComponent={sortBy('mobile')}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                resetAll={this.resetAll}
                selectedFiltersCount={selectedFiltersCountForMobile}
                isMapVariant={false}
                noResultsInfo={noResultsInfo}
                location={location}
              >
                <FilterComponent
                  key="dates"
                  id="SearchPage_Mobile.AvailabilityFilter"
                  config={{
                    key: 'dates',
                    schemaType: 'dates',
                    label: intl.formatMessage({ id: 'FilterComponent.datesLabel' }),
                    dateRangeMode: 'day',
                  }}
                  containerId="SearchPage_MobileFilters"
                  marketplaceCurrency={marketplaceCurrency}
                  urlQueryParams={validQueryParams}
                  initialValues={initialValues(this.props, this.state.currentQueryParams)}
                  getHandleChangedValueFn={this.getHandleChangedValueFn}
                  intl={intl}
                  liveEdit
                  showAsPopup={false}
                  variant="editorial"
                />
                {listingTypePathParam ? null : (
                  <FilterComponent
                    key="listingType"
                    id="SearchPage_Mobile.OfferTypeFilter"
                    config={{
                      key: 'listingType',
                      schemaType: 'listingType',
                      scope: 'public',
                      options:
                        config?.listing?.listingTypes?.map(lt => ({
                          option: lt.listingType,
                          label: lt.label,
                        })) || [],
                    }}
                    containerId="SearchPage_MobileFilters"
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={validQueryParams}
                    initialValues={initialValues(this.props, this.state.currentQueryParams)}
                    getHandleChangedValueFn={this.getHandleChangedValueFn}
                    intl={intl}
                    liveEdit
                    showAsPopup={false}
                    variant="editorial"
                  />
                )}
                {availableFilters.map(filterConfig => {
                  const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                    filterConfig.key
                  }`;
                  const filterId = `SearchFiltersMobile.${filterConfig.key.toLowerCase()}`;

                  return (
                    <FilterComponent
                      key={key}
                      id={filterId}
                      config={filterConfig}
                      containerId="SearchPage_MobileFilters"
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                      variant="editorial"
                    />
                  );
                })}
              </SearchFiltersMobile>
              {noResultsInfo}
              <div
                className={classNames(css.listingsForGridVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                <SearchErrors
                  searchListingsError={searchListingsError}
                  isValidDatesFilter={isValidDatesFilter}
                />
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={listings}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(location.search)}
                  isMapVariant={false}
                  listingTypeParam={listingTypePathParam}
                  intl={intl}
                />
              </div>
            </div>
          </div>
        </div>
        <FooterContainer />
      </Page>
    );
  }
}

/**
 * SearchPage "container" (grid layout): selects Redux state and dispatch handlers, then passes the
 * same prop surface as before to `SearchPageComponent` via `SearchPageAccessWrapper`.
 *
 * @param {Object} props - Router / route props from `routeConfiguration.js` and `Routes.js`
 * @returns {JSX.Element}
 */
const SearchPage = props => {
  const dispatch = useDispatch();
  const selectListingsById = useMemo(makeGetListingsByIdSelector, []);

  const currentUser = useSelector(state => state.user?.currentUser);
  const { pagination, searchInProgress, searchListingsError, searchParams } = useSelector(
    state => state.SearchPage
  );
  const listings = useSelector(state =>
    selectListingsById(state, state.SearchPage.currentPageResultIds)
  );
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );

  return (
    <SearchPageAccessWrapper
      {...props}
      PageComponent={SearchPageComponent}
      currentUser={currentUser}
      listings={listings}
      pagination={pagination}
      scrollingDisabled={scrollingDisabled}
      searchInProgress={searchInProgress}
      searchListingsError={searchListingsError}
      searchParams={searchParams}
      onManageDisableScrolling={onManageDisableScrolling}
    />
  );
};

export default SearchPage;
